/**
 * Product Main Section - Image Slider & Add to Cart
 * Pure JavaScript, no external libraries
 */

(function() {
  'use strict';

  // ============================================
  // IMAGE SLIDER
  // ============================================

  const ProductSlider = {
    container: null,
    mainImages: null,
    thumbnails: null,
    prevBtn: null,
    nextBtn: null,
    currentIndex: 0,
    totalImages: 0,
    thumbnailsContainer: null,

    init() {
      this.container = document.querySelector('[data-product-slider]');
      this.thumbnailsContainer = document.querySelector('[data-thumbnails-container]');
      if (!this.container) return;

      this.mainImages = this.container.querySelectorAll('[data-main-image]');
      this.thumbnails = this.thumbnailsContainer ? this.thumbnailsContainer.querySelectorAll('[data-thumbnail]') : [];
      this.prevBtn = this.container.querySelector('[data-prev-btn]');
      this.nextBtn = this.container.querySelector('[data-next-btn]');

      this.totalImages = this.mainImages.length;
      if (this.totalImages === 0) return;

      this.bindEvents();
      this.updateSlider();
    },

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.goTo(index));
      });

      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (!this.container || !this.container.contains(document.activeElement)) return;
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });

      // Touch/swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      this.container.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.container.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      }, { passive: true });
    },

    handleSwipe(startX, endX) {
      const threshold = 50;
      const diff = startX - endX;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    },

    prev() {
      this.currentIndex = (this.currentIndex - 1 + this.totalImages) % this.totalImages;
      this.updateSlider();
    },

    next() {
      this.currentIndex = (this.currentIndex + 1) % this.totalImages;
      this.updateSlider();
    },

    goTo(index) {
      this.currentIndex = index;
      this.updateSlider();
    },

    updateSlider() {
      // Update main images visibility
      this.mainImages.forEach((img, index) => {
        if (index === this.currentIndex) {
          img.classList.remove('vd49k', 're8wd');
        } else {
          img.classList.add('vd49k', 're8wd');
        }
      });

      // Update thumbnails active state
      this.thumbnails.forEach((thumb, index) => {
        const isActive = index === this.currentIndex;
        const inner = thumb.querySelector('[data-thumbnail]') || thumb;
        
        if (isActive) {
          thumb.classList.remove('mbri4');
          thumb.classList.add('wmhu3');
          thumb.setAttribute('aria-checked', 'true');
        } else {
          thumb.classList.remove('wmhu3');
          thumb.classList.add('mbri4');
          thumb.setAttribute('aria-checked', 'false');
        }
      });

      // Update button states
      if (this.prevBtn) {
        this.prevBtn.style.opacity = this.totalImages <= 1 ? '0.5' : '1';
        this.prevBtn.style.pointerEvents = this.totalImages <= 1 ? 'none' : 'auto';
      }
      if (this.nextBtn) {
        this.nextBtn.style.opacity = this.totalImages <= 1 ? '0.5' : '1';
        this.nextBtn.style.pointerEvents = this.totalImages <= 1 ? 'none' : 'auto';
      }

      // Scroll active thumbnail into view
      this.scrollActiveThumbnailIntoView();
    },

    scrollActiveThumbnailIntoView() {
      const activeThumb = this.thumbnails[this.currentIndex];
      if (activeThumb && this.thumbnailsContainer) {
        activeThumb.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest'
        });
      }
    }
  };

  // ============================================
  // VARIANT SELECTOR
  // ============================================

  const VariantSelector = {
    form: null,
    optionButtons: [],
    variantSelect: null,
    currentOptions: {},
    initialized: false,

    init() {
      this.form = document.querySelector('[data_product_form]');
      this.variantIdInput = this.form ? this.form.querySelector('#variant-id-input') : null;
      this.variantSelect = this.variantIdInput; // Alias for compatibility

      if (!this.form || !this.variantSelect) {
        return;
      }

      this.optionButtons = this.form.querySelectorAll('[data-option-input]');

      this.initCurrentOptions();
      this.bindEvents();
      this.syncButtonStatesWithSelectedVariant();
      this.updatePriceFromSelectedVariant();
      this.updateOptionButtonStates();

      this.initialized = true;
    },

    initCurrentOptions() {
      const selectedVariantId = this.variantSelect.value;

      // Find variant by string comparison since IDs can be numbers or strings
      const selectedVariant = window.productVariants?.find(v => String(v.id) === String(selectedVariantId));

      if (selectedVariant) {
        this.currentOptions = { ...selectedVariant.options };

        // If selected variant is not available, find an available one
        if (!selectedVariant.available) {
          const availableVariant = window.productVariants?.find(v => v.available);
          if (availableVariant) {
            this.currentOptions = { ...availableVariant.options };
            this.variantSelect.value = availableVariant.id;
          }
        }
      } else if (window.productVariants && window.productVariants.length > 0) {
        // Fallback to first available variant
        const firstAvailable = window.productVariants.find(v => v.available) || window.productVariants[0];
        this.currentOptions = { ...firstAvailable.options };
        this.variantSelect.value = firstAvailable.id;
      }
    },

    bindEvents() {
      this.optionButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleOptionClick(button);
        });
      });

      this.variantSelect.addEventListener('change', () => {
        this.onVariantChange();
      });
    },

    handleOptionClick(button) {
      const optionName = button.dataset.option;
      const optionValue = button.value;

      this.currentOptions[optionName] = optionValue;

      const group = button.closest('[role="radiogroup"]');
      if (group) {
        group.querySelectorAll('[data-option-input]').forEach(btn => {
          const isSelected = btn.value === optionValue && btn.dataset.option === optionName;
          btn.classList.toggle('ons40', isSelected);
          btn.classList.toggle('ssxa9', isSelected);
          btn.classList.toggle('oqcw6', isSelected);
          btn.classList.toggle('shp6i', !isSelected);
          btn.classList.toggle('yosy2', !isSelected);
          btn.classList.toggle('r1tmp', !isSelected);
          btn.setAttribute('aria-checked', isSelected.toString());
        });
      }

      this.selectMatchingVariant();
    },

    selectMatchingVariant() {
      // First try exact match
      let matchingVariant = window.productVariants?.find(variant => {
        return Object.keys(this.currentOptions).every(selectedKey => {
          const selectedValue = this.currentOptions[selectedKey];
          const optionEntry = variant.options[selectedKey];
          if (optionEntry === undefined) return true;
          return optionEntry === selectedValue;
        });
      });

      // If no exact match, try to find any available variant starting with current model option
      if (!matchingVariant) {
        const modelOption = this.currentOptions['Model'];
        if (modelOption) {
          // Find first available variant that has this model
          matchingVariant = window.productVariants?.find(variant => {
            return variant.available && variant.options['Model'] === modelOption;
          });
        }
      }

      // If still no match, just use first available variant
      if (!matchingVariant) {
        matchingVariant = window.productVariants?.find(v => v.available);
      }

      if (matchingVariant) {
        this.variantSelect.value = matchingVariant.id;
        this.currentOptions = { ...matchingVariant.options };
        this.onVariantChange();
        this.syncButtonStatesWithSelectedVariant();
      } else {
        // Update button states to show which options lead to unavailable variants
        this.updateOptionButtonStates();
      }
    },

    onVariantChange() {
      const selectedVariantId = this.variantSelect.value;
      const selectedVariant = window.productVariants?.find(v => String(v.id) === String(selectedVariantId));

      if (selectedVariant) {
        this.currentOptions = { ...selectedVariant.options };
        this.syncButtonStatesWithSelectedVariant();
        this.updatePrice(selectedVariant);
        this.updateButtonStates(selectedVariant);

        // Sync hidden variant ID input for form submission
        const variantIdInput = document.getElementById('variant-id-input');
        if (variantIdInput) {
          variantIdInput.value = selectedVariantId;
        }

        // Update Shop Pay payment terms widget
        this.updateShopPayWidget(selectedVariant);
      }
    },

    updateShopPayWidget(variant) {
      const paymentTermsEl = document.querySelector('[data-shopify="payment-terms"]');
      if (!paymentTermsEl) return;

      // Update attributes Shopify uses
      paymentTermsEl.setAttribute('data-shopify-id', variant.id);
      paymentTermsEl.setAttribute('data-shopify-price', Math.round(parseFloat(variant.price) * 100));

      // Refresh the Shopify PaymentTerms widget
      if (window.Shopify?.PaymentTerms?.refresh) {
        window.Shopify.PaymentTerms.refresh();
      }
    },

    syncButtonStatesWithSelectedVariant() {
      const variantId = this.variantSelect.value;
      const variant = window.productVariants?.find(v => String(v.id) === String(variantId));

      if (!variant) {
        return;
      }

      this.optionButtons.forEach(button => {
        const optionName = button.dataset.option;
        const optionValue = button.value;
        const isSelected = variant.options[optionName] === optionValue;

        button.classList.toggle('ons40', isSelected);
        button.classList.toggle('ssxa9', isSelected);
        button.classList.toggle('oqcw6', isSelected);
        button.classList.toggle('shp6i', !isSelected);
        button.classList.toggle('yosy2', !isSelected);
        button.classList.toggle('r1tmp', !isSelected);
        button.setAttribute('aria-checked', isSelected.toString());
      });
    },

    updatePriceFromSelectedVariant() {
      const selectedVariantId = this.variantSelect.value;
      const selectedVariant = window.productVariants?.find(v => String(v.id) === String(selectedVariantId));

      if (selectedVariant) {
        this.updatePrice(selectedVariant);
        this.updateButtonStates(selectedVariant);
      }
    },

    updatePrice(variant) {
      const priceEl = document.querySelector('[data-product-price]');
      const comparePriceEl = document.querySelector('[data-compare-price]');
      const discountBadge = document.querySelector('[data-discount-badge]');

      if (priceEl && variant) {
        const formattedPrice = this.formatMoney(variant.price);
        priceEl.textContent = formattedPrice;
      }

      if (comparePriceEl && variant) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';

          if (discountBadge) {
            const savingsCents = variant.compare_at_price - variant.price;
            const savingsFormatted = this.formatMoney(savingsCents);
            discountBadge.textContent = 'You Save ' + savingsFormatted;
            discountBadge.style.display = '';
          }
        } else {
          comparePriceEl.style.display = 'none';
          if (discountBadge) {
            discountBadge.style.display = 'none';
          }
        }
      }
    },

    updateButtonStates(variant) {
      const addToCartBtn = document.querySelector('[data-add-to-cart-btn]');
      const buyNowBtn = document.querySelector('[data-buy-now-btn]');

      if (variant.available) {
        addToCartBtn.textContent = 'Get My ' + (window.productBrandName || 'Hidden') + ' Dash Cam';
        addToCartBtn.disabled = false;
        if (buyNowBtn) buyNowBtn.disabled = false;
      } else {
        addToCartBtn.textContent = 'Sold Out';
        addToCartBtn.disabled = true;
        if (buyNowBtn) buyNowBtn.disabled = true;
      }
    },

    updateOptionButtonStates() {
      // Check if there's ANY available variant at all
      const hasAnyAvailableVariant = window.productVariants?.some(v => v.available);

      // If no variants are available at all, disable all option buttons
      if (!hasAnyAvailableVariant) {
        this.optionButtons.forEach(btn => {
          btn.classList.add('is-unavailable');
          btn.disabled = true;
        });
        return;
      }

      // Group buttons by option name
      const optionGroups = {};
      this.optionButtons.forEach(btn => {
        const optionName = btn.dataset.option;
        if (!optionGroups[optionName]) {
          optionGroups[optionName] = [];
        }
        optionGroups[optionName].push(btn);
      });

      // For each option group, check which values lead to available variants
      Object.keys(optionGroups).forEach(optionName => {
        const buttons = optionGroups[optionName];

        buttons.forEach(btn => {
          const optionValue = btn.value;

          // Check if ANY variant with this option value is available (regardless of other selections)
          const hasAvailableVariant = window.productVariants?.some(variant => {
            // Check if variant has this option with this value
            if (variant.options[optionName] !== optionValue) {
              return false;
            }
            // Only check if variant is available, don't restrict by other selected options
            return variant.available;
          });

          // Add/remove unavailable class
          btn.classList.toggle('is-unavailable', !hasAvailableVariant);
          btn.disabled = !hasAvailableVariant;
        });
      });
    },

    formatMoney(cents) {
      if (cents === null || cents === undefined || cents === '') return '';
      const dollars = cents / 100;
      return '$' + dollars.toFixed(2);
    }
  };

  // ============================================
  // CART DRAWER
  // ============================================

  const CartDrawer = {
    cartItemsContainer: null,
    cartTitle: null,
    cartSubtotal: null,
    cartTotal: null,
    cartFooter: null,
    cartSaving: null,

    init() {
      this.cartItemsContainer = document.querySelector('[data-cart-items]');
      this.cartTitle = document.querySelector('[data-cart-title]');
      this.cartSubtotal = document.querySelector('[data-cart-subtotal]');
      this.cartTotal = document.querySelector('[data-cart-total]');
      this.cartFooter = document.querySelector('[data-cart-footer]');
      this.cartSaving = document.querySelector('[data-cart-saving-value]');
    },

    async open() {
      await this.fetchAndRender();
      window.FuvyAddToCart.openCartDrawer();
    },

    async fetchAndRender() {
      try {
        const cart = await this.fetchCart();
        console.log('[Cart Debug] fetchAndRender - cart items:', cart.items.length);

        if (cart.items.length === 0) {
          this.renderEmpty();
        } else {
          this.renderItems(cart);
          this.updateTotals(cart);
        }
      } catch (error) {
        console.error('Error loading cart:', error);
        this.renderEmpty();
      }
    },

    fetchCart() {
      return fetch('/cart.js')
        .then(res => res.json());
    },

    renderEmpty() {
      console.log('[Cart Debug] renderEmpty called');
      if (this.cartTitle) this.cartTitle.textContent = 'Your Cart';
      if (this.cartItemsContainer) {
        this.cartItemsContainer.innerHTML = '<li class="iziin ig6cr nkti2 t8olb"><p class="s0dgi iziin">Your cart is empty</p></li>';
      }
      if (this.cartFooter) this.cartFooter.style.display = 'none';
    },

    renderItems(cart) {
      if (this.cartTitle) this.cartTitle.textContent = `Your Cart (${cart.item_count})`;
      if (this.cartFooter) this.cartFooter.style.display = '';

      if (!this.cartItemsContainer) return;

      this.cartItemsContainer.innerHTML = cart.items.map(item => `
        <li class="ig6cr nkti2 t8olb xhldu qcr47" data-cart-item data-key="${item.key}">
          <div class="ig6cr">
            <div class="nn6hu kxsvb">
              <img class=" tiar1 kib8b" alt="${item.title}" src="${item.image}">
            </div>
            <div class="xpr5v ev9zf">
              <div>
                <h3 class="xiglx e6gnn iziin k0th0">${item.title}</h3>
                <div class="ig6cr q4rmh ndrmp cgl6f">
                  <p class="s0dgi iziin cart-item-variant-title">${item.variant_title || 'Default'}</p>
                </div>
              </div>
              <div class="kgj10 r1tmp nic7m ig6cr cva65 qnnq7 kz6oa ewddl kib8b roqcw">
                <button type="button" class="shp6i yosy2 ig6cr cva65 ewuqd ndrmp g9tkw nxjj8" data-cart-decrease data-key="${item.key}">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 12H18.0007" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                </button>
                <div class="shp6i ig6cr ohvai ndrmp g9tkw" data-cart-quantity>${item.quantity}</div>
                <button type="button" class="shp6i yosy2 ig6cr cva65 ewuqd ndrmp g9tkw nxjj8" data-cart-increase data-key="${item.key}">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 12.0002H18.0007M12.0002 6V18.0007" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                </button>
              </div>
            </div>
          </div>
          <div class="ig6cr ovc25 lhvt3 nkti2">
            <p class="shp6i iziin k0th0">${this.formatPrice(item.final_line_price)}</p>
            <button type="button" class="s0dgi inbev qi4b0 kib8b xpyel" data-cart-remove data-key="${item.key}">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5.25 5.75V19.75C5.25 20.5784 5.92157 21.25 6.75 21.25H17.25C18.0784 21.25 18.75 20.5784 18.75 19.75V5.75M4 5.75H19.999M5.25 15.8955V9.89551M18.75 15.8955V9.89551M10 16.5V10.5M14 16.5V10.5M15.2495 5.75V4.25C15.2495 3.42157 14.5779 2.75 13.7495 2.75H10.2495C9.42108 2.75 8.74951 3.42157 8.74951 4.25V5.75H15.2495Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </button>
          </div>
        </li>
      `).join('');

      this.bindItemEvents();
    },

    bindItemEvents() {
      document.querySelectorAll('[data-cart-remove]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const key = e.currentTarget.dataset.key;
          this.removeItem(key);
        });
      });

      document.querySelectorAll('[data-cart-decrease]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const key = e.currentTarget.dataset.key;
          this.changeQuantity(key, -1);
        });
      });

      document.querySelectorAll('[data-cart-increase]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const key = e.currentTarget.dataset.key;
          this.changeQuantity(key, 1);
        });
      });
    },

    updateTotals(cart) {
      if (this.cartSubtotal) this.cartSubtotal.textContent = this.formatPrice(cart.items_subtotal_price);
      if (this.cartTotal) this.cartTotal.textContent = this.formatPrice(cart.items_subtotal_price); // + shipping + tax
      if (this.cartSaving) this.cartSaving.textContent = this.formatPrice(cart.item_count * 3000);
    },

    async removeItem(key) {
      try {
        console.log('[Cart Debug] removeItem called for key:', key);
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        await this.fetchAndRender();
        window.FuvyAddToCart.updateCartCount();
        console.log('[Cart Debug] Dispatching theme:cart:updated');
        document.dispatchEvent(new CustomEvent('theme:cart:updated'));
      } catch (error) {
        console.error('Error removing item:', error);
      }
    },

    async changeQuantity(key, delta) {
      const itemEl = document.querySelector(`[data-cart-item][data-key="${key}"]`);
      const quantityEl = itemEl?.querySelector('[data-cart-quantity]');
      if (!quantityEl) return;

      const currentQty = parseInt(quantityEl.textContent);
      const newQty = currentQty + delta;

      if (newQty < 1) {
        this.removeItem(key);
        return;
      }

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty })
        });
        await this.fetchAndRender();
        window.FuvyAddToCart.updateCartCount();
      } catch (error) {
        console.error('Error updating quantity:', error);
      }
    },

    formatPrice(cents) {
      if (cents === null || cents === undefined || cents === '') return '$0.00';
      const dollars = cents / 100;
      return '$' + dollars.toFixed(2);
    }
  };

  // ============================================
  // ADD TO CART & BUY NOW
  // ============================================

  const SKIP_ATC_DRAWER = true;

  const AddToCart = {
    form: null,
    addToCartBtn: null,
    buyNowBtn: null,
    isLoading: false,

    init() {
      this.form = document.querySelector('[data_product_form]');
      this.addToCartBtn = document.querySelector('[data-add-to-cart-btn]');
      this.buyNowBtn = document.querySelector('[data-buy-now-btn]');

      if (!this.form) {
        return;
      }

      this.bindEvents();
    },

    bindEvents() {
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.addToCart();
        });
      }

      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.buyNow();
        });
      }

      // Handle form submission
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });

      // Handle sticky add-to-cart click
      this.form.addEventListener('sticky-atc-click', (e) => {
        console.log('[DEBUG] product-main.js: Received sticky-atc-click event');
        if (this.addToCartBtn) {
          console.log('[DEBUG] product-main.js: Clicking add-to-cart button');
          this.addToCartBtn.click();
        } else {
          console.log('[DEBUG] product-main.js: No add-to-cart button found!');
        }
      });
    },

    async     addToCart() {
      if (this.isLoading) return;

      const formData = new FormData(this.form);
      const variantId = formData.get('id');

      if (!variantId) {
        this.showError('Please select product options');
        return;
      }

      this.setLoading(true, 'add-to-cart');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        const cartData = await response.json();

        if (SKIP_ATC_DRAWER) {
          window.location.href = '/checkout';
          return;
        }

        this.onCartAdded(cartData);
        this.openCartDrawer();

      } catch (error) {
        this.showError(error.message);
        this.setLoading(false, 'add-to-cart');
      } finally {
        if (!SKIP_ATC_DRAWER) {
          this.setLoading(false, 'add-to-cart');
        }
      }
    },

    async     buyNow() {
      if (this.isLoading) return;

      const formData = new FormData(this.form);
      const variantId = formData.get('id');

      if (!variantId) {
        this.showError('Please select product options');
        return;
      }

      this.setLoading(true, 'buy-now');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        window.location.href = '/checkout';

      } catch (error) {
        this.showError(error.message);
        this.setLoading(false, 'buy-now');
      }
    },

    onCartAdded(cartItem) {
      this.updateCartCount();
      this.showSuccess('Added to cart!');
      CartDrawer.open();
    },

      openCartDrawer() {
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      const overlay = document.querySelector('[data-cart-drawer-overlay]');
      if (cartDrawer) {
        cartDrawer.classList.add('is-open');
        document.body.classList.add('cart-drawer-open');
      }
      if (overlay) {
        overlay.classList.add('is-open');
      }
      document.dispatchEvent(new CustomEvent('theme:cart:open'));
    },

    closeCartDrawer() {
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      const overlay = document.querySelector('[data-cart-drawer-overlay]');
      if (cartDrawer) {
        cartDrawer.classList.remove('is-open');
        document.body.classList.remove('cart-drawer-open');
      }
      if (overlay) {
        overlay.classList.remove('is-open');
      }
    },

    updateCartCount() {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          const cartCount = document.querySelector('[data-cart-count]');
          if (cartCount) {
            cartCount.textContent = cart.item_count;
            cartCount.classList.add('has-items');
          }
        })
        .catch(() => {});
    },

    setLoading(loading, buttonType) {
      this.isLoading = loading;

      if (buttonType === 'add-to-cart' && this.addToCartBtn) {
        this.addToCartBtn.classList.toggle('is-loading', loading);
        this.addToCartBtn.disabled = loading;
        this.addToCartBtn.textContent = loading ? 'Adding...' : 'Get My ' + (window.productBrandName || 'Hidden') + ' Dash Cam';
      }

      if (buttonType === 'buy-now' && this.buyNowBtn) {
        this.buyNowBtn.classList.toggle('is-loading', loading);
        this.buyNowBtn.disabled = loading;
        this.buyNowBtn.textContent = loading ? 'Processing...' : 'Buy Now';
      }
    },

    showSuccess(message) {
      this.showNotification(message, 'success');
    },

    showError(message) {
      this.showNotification(message, 'error');
    },

    showNotification(message, type) {
      const existing = document.querySelector('.product-notification');
      if (existing) existing.remove();

      const notification = document.createElement('div');
      notification.className = `product-notification product-notification--${type}`;
      notification.innerHTML = `
        <span>${message}</span>
        <button class="product-notification__close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      `;

      document.body.appendChild(notification);

      requestAnimationFrame(() => {
        notification.classList.add('is-visible');
      });

      const timeout = setTimeout(() => {
        this.dismissNotification(notification);
      }, 3000);

      notification.querySelector('.product-notification__close').addEventListener('click', () => {
        clearTimeout(timeout);
        this.dismissNotification(notification);
      });
    },

    dismissNotification(notification) {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }
  };

  // ============================================
  // CART PAGE
  // ============================================

  const CartPage = {
    itemsContainer: null,
    subtotal: null,
    total: null,

    init() {
      this.itemsContainer = document.querySelector('[data-cart-page-items]');
      this.subtotal = document.querySelector('[data-cart-page-subtotal]');
      this.total = document.querySelector('[data-cart-page-total]');

      if (!this.itemsContainer) return;

      this.bindEvents();
    },

    bindEvents() {
      document.querySelectorAll('[data-cart-remove]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const key = e.currentTarget.dataset.key;
          this.removeItem(key);
        });
      });

      document.querySelectorAll('[data-cart-decrease]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const key = e.currentTarget.dataset.key;
          this.changeQuantity(key, -1);
        });
      });

      document.querySelectorAll('[data-cart-increase]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const key = e.currentTarget.dataset.key;
          this.changeQuantity(key, 1);
        });
      });
    },

    async removeItem(key) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        location.reload();
      } catch (error) {
        console.error('Error removing item:', error);
      }
    },

    async changeQuantity(key, delta) {
      const itemEl = document.querySelector(`[data-cart-item][data-key="${key}"]`);
      const quantityEl = itemEl?.querySelector('[data-cart-quantity]');
      if (!quantityEl) return;

      const currentQty = parseInt(quantityEl.textContent);
      const newQty = currentQty + delta;

      if (newQty < 1) {
        this.removeItem(key);
        return;
      }

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty })
        });
        location.reload();
      } catch (error) {
        console.error('Error updating quantity:', error);
      }
    },

    formatPrice(cents) {
      if (cents === null || cents === undefined || cents === '') return '$0.00';
      const dollars = cents / 100;
      return '$' + dollars.toFixed(2);
    },

    async applyDiscount() {
      const discountInput = document.getElementById('discount_code');
      const discountButton = document.querySelector('[data-apply-discount]');

      if (!discountInput || !discountButton) return;

      const discountCode = discountInput.value.trim();

      if (!discountCode) {
        alert('Please enter a discount code');
        return;
      }

      discountButton.disabled = true;
      discountButton.textContent = 'Applying...';

      try {
        // Create hidden form and submit to /cart with discount parameter
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/cart';
        form.style.display = 'none';

        const discountField = document.createElement('input');
        discountField.type = 'hidden';
        discountField.name = 'discount';
        discountField.value = discountCode;
        form.appendChild(discountField);

        document.body.appendChild(form);
        form.submit();
      } catch (error) {
        console.error('Error applying discount:', error);
        discountButton.disabled = false;
        discountButton.textContent = 'Apply';
        alert('Failed to apply discount code. Please try again.');
      }
    },

    init() {
      this.itemsContainer = document.querySelector('[data-cart-page-items]');
      this.subtotal = document.querySelector('[data-cart-page-subtotal]');
      this.total = document.querySelector('[data-cart-page-total]');

      if (!this.itemsContainer) return;

      this.bindEvents();
      this.bindDiscountEvents();
    },

    bindDiscountEvents() {
      const discountButton = document.querySelector('[data-apply-discount]');
      if (discountButton) {
        discountButton.addEventListener('click', () => this.applyDiscount());
      }

      // Allow pressing Enter in discount input
      const discountInput = document.getElementById('discount_code');
      if (discountInput) {
        discountInput.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.applyDiscount();
          }
        });
      }
    },
  };



  // ============================================
  // MOBILE DRAWER
  // ============================================

  const MobileDrawer = {
    drawers: null,
    overlays: null,
    isOpen: false,

    init() {
      this.drawers = document.querySelectorAll('[data-mobile-drawer]');
      this.overlays = document.querySelectorAll('[data-mobile-drawer-overlay]');
      
      if (this.drawers.length === 0) return;

      this.bindTrigger();
      this.bindCloseButtons();
      this.bindOverlays();
      this.bindEscapeKey();
    },

    bindTrigger() {
      const trigger = document.querySelector('#mobile-menu-trigger');
      if (trigger) {
        trigger.addEventListener('click', () => this.toggle());
      }
    },

    bindCloseButtons() {
      // Find the close button specifically by class inside drawers
      this.drawers.forEach(drawer => {
        const closeBtn = drawer.querySelector('.mobile-drawer-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.close();
          });
        }
      });
    },

    bindOverlays() {
      this.overlays.forEach(overlay => {
        overlay.addEventListener('click', () => this.close());
      });
    },

    bindEscapeKey() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    open() {
      this.isOpen = true;
      this.drawers.forEach(drawer => {
        drawer.classList.add('is-open');
      });
      this.overlays.forEach(overlay => {
        overlay.classList.add('is-open');
      });
      document.body.classList.add('mobile-drawer-open');
    },

    close() {
      this.isOpen = false;
      this.drawers.forEach(drawer => {
        drawer.classList.remove('is-open');
      });
      this.overlays.forEach(overlay => {
        overlay.classList.remove('is-open');
      });
      document.body.classList.remove('mobile-drawer-open');
    }
  };

  // ============================================
  // INITIALIZATION
  // ============================================

  // Expose AddToCart for global access
  window.FuvyAddToCart = AddToCart;

  // Initialize cart drawer (close button, overlay click) - runs on every page
  function initCartDrawer() {
    CartDrawer.init();
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-drawer-overlay]');
    if (!cartDrawer) return;

    // Close button
    const closeBtn = cartDrawer.querySelector('button');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => window.FuvyAddToCart.closeCartDrawer());
    }

    // Click overlay to close
    if (overlay) {
      overlay.addEventListener('click', () => window.FuvyAddToCart.closeCartDrawer());
    }
  }

  // Initialize cart drawer toggle - runs on every page
  function initCartDrawerToggle() {
    const toggleBtns = document.querySelectorAll('[data-cart-drawer-toggle]');
    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => CartDrawer.open());
    });
  }

  function initProductMain() {
    ProductSlider.init();
    VariantSelector.init();
    AddToCart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCartDrawer();
      initCartDrawerToggle();
      initProductMain();
      CartPage.init();
      MobileDrawer.init();
    });
  } else {
    initCartDrawer();
    initCartDrawerToggle();
    initProductMain();
    CartPage.init();
    MobileDrawer.init();
  }

  // Re-initialize on Shopify section reload
  if (typeof window.theme !== 'undefined' && window.theme.onSectionLoad) {
    window.theme.onSectionLoad('product-main', initProductMain);
  }

})();
